import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Users, Plus, Calendar, ArrowRight } from 'lucide-react';
import api from '../services/api';
import { Team, PaginatedResponse, PaginationMeta } from '../types';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { cn } from '../lib/utils';
import { TeamListSkeleton } from './SkeletonLoaders';
import EmptyState from './EmptyState';

const TeamList: React.FC = () => {
  const [teams, setTeams] = useState<Team[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [pagination, setPagination] = useState<PaginationMeta | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchTeams = async () => {
      try {
        setLoading(true);
        // Backend returns PaginatedResponse wrapped in WriteSuccess
        const response = await api.get<{ data: PaginatedResponse<Team> }>(`/teams?page=${page}&limit=${limit}`);
        const paginatedData = response.data.data;
        setTeams(Array.isArray(paginatedData?.data) ? paginatedData.data : []);
        setPagination(paginatedData?.meta || null);
        setError(null);
      } catch (err: any) {
        console.error('Failed to fetch teams:', err);
        setError(err.response?.data?.error?.message || 'Failed to load teams');
      } finally {
        setLoading(false);
      }
    };

    fetchTeams();
  }, [page, limit]);

  const handleTeamClick = (teamId: string) => {
    navigate(`/teams/${teamId}`);
  };

  if (loading) {
    return <TeamListSkeleton />;
  }

  if (error) {
    return (
      <EmptyState
        emoji="⚠️"
        title="Failed to load teams"
        description={error}
        action={{
          label: 'Retry',
          onClick: () => window.location.reload(),
        }}
      />
    );
  }

  if (teams.length === 0) {
    return (
      <EmptyState
        icon={Users}
        title="No teams yet"
        description="Create your first team to start collaborating"
        action={{
          label: 'Create a Team',
          onClick: () => navigate('/teams/create'),
          icon: Plus,
        }}
      />
    );
  }

  return (
    <Card className="shadow-sm border">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-lg">
            <Users className="h-5 w-5 text-primary" />
            My Teams
          </CardTitle>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/teams/create')}
            className="h-8 text-sm gap-1"
          >
            <Plus className="h-4 w-4" />
            New
          </Button>
        </div>
      </CardHeader>

      <CardContent>
        <div className="grid grid-cols-1 gap-4">
          {teams.map((team, index) => (
            <div
              key={team.id}
              onClick={() => handleTeamClick(team.id)}
              role="button"
              tabIndex={0}
              style={{ animationDelay: `${index * 50}ms` }}
              className={cn(
                "group relative rounded-none border-2 border-[#161E54] bg-white p-4 transition-all hover:shadow-neo hover:-translate-y-1 cursor-pointer",
                "animate-fade-in"
              )}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleTeamClick(team.id);
                }
              }}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 space-y-2">
                  <h3 className="text-base font-semibold text-gray-900 leading-tight tracking-tight">
                    {team.name}
                  </h3>
                  <p className="text-sm text-muted-foreground line-clamp-2">
                    {team.description || 'No description'}
                  </p>
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <Calendar className="h-3 w-3" />
                    <span>Created {new Date(team.createdAt).toLocaleDateString()}</span>
                  </div>
                </div>
                <ArrowRight className="h-5 w-5 text-muted-foreground group-hover:translate-x-1 transition-transform flex-shrink-0" />
              </div>
            </div>
          ))}
        </div>

        {/* Pagination Controls */}
        {pagination && pagination.totalPages > 1 && (
          <div className="flex justify-center items-center gap-4 mt-6 pt-4 border-t">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1 || loading}
            >
              Previous
            </Button>
            <span className="text-sm text-muted-foreground">
              Page {pagination.currentPage} of {pagination.totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage((p) => Math.min(pagination.totalPages, p + 1))}
              disabled={page === pagination.totalPages || loading}
            >
              Next
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default TeamList;
